import { useState, useEffect, useCallback } from 'react';
import {
  Database, Search, Download, FileSpreadsheet,
  FileText, FileDown, Clock, Calendar, RefreshCw, Trash2, Pause,
  ChevronDown, ChevronRight, X, Filter, Eye, Plus, Check,
  Play, Edit2, FileIcon, LayoutList, ClipboardList, AlertCircle, Lock
} from 'lucide-react';
import questdbService from '../services/questdb';
import mysqlService from '../services/mysql';
import reportService from '../services/reportService';
import authService from '../services/auth';
import LockedFeature from './common/LockedFeature.jsx';
import eventStream from '../services/eventStream.js';
import TemplatesView from './TemplatesView';

// ─── Top-level view: 'new-report' | 'scheduled' | 'generated-files'
export default function Reporting({ onBack }) {
  const user = authService.getUser();

  if (!authService.hasFeature('reports')) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0a0e1a', color: '#e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
      }}>
        <div style={{
          maxWidth: 480, textAlign: 'center', background: '#0f172a',
          border: '1px solid #1e293b', borderRadius: 12, padding: 32
        }}>
          <AlertCircle size={36} color="#fbbf24" style={{ marginBottom: 12 }} />
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Reports is locked</h2>
          <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 18px', lineHeight: 1.5 }}>
            This feature is not enabled for your account. Contact your administrator
            to request access.
          </p>
          <button
            onClick={onBack}
            style={{
              padding: '8px 18px', borderRadius: 8, background: '#6366f1',
              color: '#fff', border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer'
            }}
          >Back to Dashboards</button>
        </div>
      </div>
    );
  }

  const plantAccess = user?.plantAccess && user.plantAccess.length > 0
    ? user.plantAccess
    : user?.plantId ? [{ plantId: user.plantId, role: 'operator' }] : [];

  // ── View state
  const [view, setView] = useState('generated-files');

  // ── Database engine
  const [dbEngine, setDbEngine] = useState('questdb');
  const [mysqlDatabase, setMysqlDatabase] = useState('');
  const [mysqlDatabases, setMysqlDatabases] = useState([]);
  // MySQL data source: one of the selected plant's registered connections.
  const [mysqlInstanceId, setMysqlInstanceId] = useState('');
  const [mysqlInstances, setMysqlInstances] = useState([]);

  // ── Plant & table
  const [selectedPlant, setSelectedPlant] = useState(plantAccess[0]?.plantId || '');
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [loadingTables, setLoadingTables] = useState(false);

  // ── Columns
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');

  // ── Distinct
  const [distinctColumn, setDistinctColumn] = useState('');
  const [distinctValues, setDistinctValues] = useState([]);
  const [selectedDistinctValues, setSelectedDistinctValues] = useState([]);
  const [loadingDistinct, setLoadingDistinct] = useState(false);
  const [distinctSearch, setDistinctSearch] = useState('');

  // ── SQL & preview
  const [sql, setSql] = useState('');
  // Once the user types in the SQL textarea, stop the auto-generator from
  // overwriting their query. They can hit "Reset SQL" to opt back in.
  const [sqlIsDirty, setSqlIsDirty] = useState(false);

  // ── WHERE filter builder (panel-style AND-only rows)
  const [filters, setFilters] = useState([]); // [{ field, op, value }]
  const [previewData, setPreviewData] = useState([]);
  const [previewPage, setPreviewPage] = useState(1);
  const PREVIEW_PAGE_SIZE = 20;
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewCount, setPreviewCount] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ── Time range (matches screenshot UI)
  const [timeRangeTab, setTimeRangeTab] = useState('specific'); // 'specific' | 'duration'
  const [specificDirection, setSpecificDirection] = useState('Before');
  const [specificDate, setSpecificDate] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');

  // ── Section collapse
  const [dataSourceOpen, setDataSourceOpen] = useState(true);
  const [timeRangeOpen, setTimeRangeOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sqlOpen, setSqlOpen] = useState(true);

  // ── Export modal
  const [showExportModal, setShowExportModal] = useState(false);

  // ── Export
  const [exportFormat, setExportFormat] = useState('excel');
  const [excelMode, setExcelMode] = useState('new');
  const [appendFile, setAppendFile] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState('');
  const [exportLimits, setExportLimits] = useState(null);

  // ── Schedule
  const [scheduleMode, setScheduleMode] = useState('onetime');
  const [scheduleInterval, setScheduleInterval] = useState('every_hour');
  const [customInterval, setCustomInterval] = useState(5);
  const [customUnit, setCustomUnit] = useState('minutes');
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleExcelMode, setScheduleExcelMode] = useState('new');
  const [scheduleFormat, setScheduleFormat] = useState('excel');

  // ── Lists
  const [scheduledReports, setScheduledReports] = useState([]);
  const [reportFiles, setReportFiles] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── List filters
  const [fileSearch, setFileSearch] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('');
  const [schedSearch, setSchedSearch] = useState('');
  const [schedTypeFilter, setSchedTypeFilter] = useState('');

  // ─────────────────────────────────────────────────────────────────────
  // Data fetching
  // ─────────────────────────────────────────────────────────────────────

  // The plant's MySQL data sources; with exactly one it is picked automatically.
  useEffect(() => {
    if (dbEngine !== 'mysql' || !selectedPlant) { setMysqlInstances([]); return; }
    let cancelled = false;
    mysqlService.listInstances(selectedPlant)
      .then(list => {
        if (cancelled) return;
        setMysqlInstances(list);
        setMysqlInstanceId(cur => (list.some(i => i.instanceId === cur) ? cur : (list.length === 1 ? list[0].instanceId : '')));
      })
      .catch(err => { if (!cancelled) { console.error('MySQL data sources error:', err); setMysqlInstances([]); } });
    return () => { cancelled = true; };
  }, [dbEngine, selectedPlant]);

  const mysqlTarget = { instanceId: mysqlInstanceId, plantId: selectedPlant };

  // Fetch MySQL databases when engine or data source changes
  useEffect(() => {
    if (dbEngine === 'mysql') {
      mysqlService.getDatabases(mysqlTarget).then(result => {
        const dbs = result.map(row => Object.values(row)[0])
          .filter(db => !['information_schema', 'performance_schema', 'mysql', 'sys'].includes(db));
        setMysqlDatabases(dbs);
      }).catch(err => { setMysqlDatabases([]); console.error('MySQL databases error:', err); });
    }
  }, [dbEngine, mysqlInstanceId, selectedPlant]);

  // Fetch tables — route based on engine
  useEffect(() => {
    if (dbEngine === 'mysql') {
      if (!mysqlDatabase) { setTables([]); return; }
      setLoadingTables(true);
      mysqlService.getTables(mysqlDatabase, mysqlTarget)
        .then(r => setTables(r.map(x => Object.values(x)[0]).filter(Boolean)))
        .catch(err => console.error(err))
        .finally(() => setLoadingTables(false));
    } else {
      if (!selectedPlant) return;
      setLoadingTables(true);
      questdbService.getTables(selectedPlant)
        .then(r => setTables(r.map(x => x.table_name).filter(Boolean)))
        .catch(err => console.error(err))
        .finally(() => setLoadingTables(false));
    }
  }, [selectedPlant, dbEngine, mysqlDatabase, mysqlInstanceId]);

  // Fetch columns — route based on engine
  useEffect(() => {
    if (!selectedTable) return;
    if (dbEngine !== 'mysql' && !selectedPlant) return;
    setLoadingColumns(true);
    setColumns([]); setSelectedColumns([]);
    setDistinctColumn(''); setDistinctValues([]); setSelectedDistinctValues([]);
    setFilters([]); setSqlIsDirty(false);
    const dbOpts = { dbEngine, mysqlDatabase, mysqlInstanceId };
    reportService.getColumns(selectedPlant || '_', selectedTable, dbOpts)
      .then(cols => { setColumns(cols); setSelectedColumns(cols.map(c => c.name)); })
      .catch(err => console.error(err))
      .finally(() => setLoadingColumns(false));
  }, [selectedTable, selectedPlant, dbEngine, mysqlDatabase, mysqlInstanceId]);

  // Fetch distinct values — route based on engine
  useEffect(() => {
    if (!distinctColumn || !selectedTable) return;
    if (dbEngine !== 'mysql' && !selectedPlant) return;
    setLoadingDistinct(true);
    setDistinctValues([]); setSelectedDistinctValues([]);
    const dbOpts = { dbEngine, mysqlDatabase, mysqlInstanceId };
    reportService.getDistinctValues(selectedPlant || '_', selectedTable, distinctColumn, dbOpts)
      .then(setDistinctValues)
      .catch(err => console.error(err))
      .finally(() => setLoadingDistinct(false));
  }, [distinctColumn, selectedTable, selectedPlant, dbEngine, mysqlDatabase, mysqlInstanceId]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const [reports, files] = await Promise.all([
        reportService.getScheduledReports().catch(() => []),
        reportService.getReportFiles().catch(() => [])
      ]);
      setScheduledReports(reports);
      setReportFiles(files);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    reportService.getExportLimits()
      .then(d => setExportLimits(d.limits))
      .catch(() => { /* non-blocking — banner just won't show */ });
  }, []);

  // Real-time updates via SSE — replaces 30s polling. We refetch the inbox
  // whenever a scheduled report finishes generating, an async export finishes,
  // or an export job's status transitions. A 60s fallback poll keeps things
  // self-healing in case SSE drops without reconnecting.
  useEffect(() => {
    const offs = [
      eventStream.subscribe('report.generated', () => loadHistory()),
      eventStream.subscribe('report.failed',    () => loadHistory()),
      eventStream.subscribe('export.done',      () => loadHistory()),
      eventStream.subscribe('export.failed',    () => loadHistory()),
      eventStream.subscribe('export.cancelled', () => loadHistory()),
      // export.progress also fires repeatedly during long exports — only refetch
      // every 5s for those so the progress bars don't trigger a refetch storm
      eventStream.subscribe('export.started',   () => loadHistory())
    ];
    const safety = setInterval(loadHistory, 60_000);
    return () => {
      offs.forEach(off => off());
      clearInterval(safety);
    };
  }, [loadHistory]);

  // ─────────────────────────────────────────────────────────────────────
  // Auto-generate SQL
  // ─────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Once the user has hand-edited the SQL, never overwrite it from selections.
    if (sqlIsDirty) return;
    if (!selectedTable || selectedColumns.length === 0) { setSql(''); return; }

    const isMySQL = dbEngine === 'mysql';
    const q = (name) => isMySQL ? `\`${name}\`` : name;
    const cols = selectedColumns.map(q).join(', ');
    let query = `SELECT ${cols} FROM ${q(selectedTable)}`;
    const whereClauses = [];
    const tsCol = columns.find(c => c.type === 'TIMESTAMP')?.name;
    const tsField = tsCol ? q(tsCol) : null;

    // Time range only applies for QuestDB (MySQL has no enforced timestamp column).
    if (!isMySQL && tsField) {
      if (timeRangeTab === 'specific' && specificDate) {
        const op = specificDirection === 'Before' ? '<' : '>';
        whereClauses.push(`${tsField} ${op} '${specificDate}'`);
      } else if (timeRangeTab === 'duration' && timeFrom && timeTo) {
        whereClauses.push(`${tsField} >= '${timeFrom}' AND ${tsField} <= '${timeTo}'`);
      }
    }

    if (distinctColumn && selectedDistinctValues.length > 0) {
      const vals = selectedDistinctValues.map(v => `'${v}'`).join(', ');
      whereClauses.push(`${q(distinctColumn)} IN (${vals})`);
    }

    // Panel-style filter rows (AND only). Skip incomplete rows.
    for (const f of filters) {
      if (!f.field) continue;
      const fld = q(f.field);
      const op = f.op || '=';
      const v = (f.value ?? '').toString();
      if (op === 'IS NULL') { whereClauses.push(`${fld} IS NULL`); continue; }
      if (op === 'IS NOT NULL') { whereClauses.push(`${fld} IS NOT NULL`); continue; }
      if (!v.trim()) continue;
      const esc = (x) => `'${String(x).replace(/'/g, "''")}'`;
      if (op === 'IN' || op === 'NOT IN') {
        const parts = v.split(',').map(s => s.trim()).filter(Boolean).map(esc).join(', ');
        if (parts) whereClauses.push(`${fld} ${op} (${parts})`);
      } else if (op === 'BETWEEN') {
        const [a, b] = v.split(',').map(s => s.trim());
        if (a && b) whereClauses.push(`${fld} BETWEEN ${esc(a)} AND ${esc(b)}`);
      } else if (op === 'LIKE') {
        whereClauses.push(`${fld} LIKE ${esc(v)}`);
      } else {
        // numeric or string — wrap as string literal; MySQL/QuestDB coerce as needed
        whereClauses.push(`${fld} ${op} ${esc(v)}`);
      }
    }

    if (whereClauses.length > 0) query += ' WHERE ' + whereClauses.join(' AND ');
    if (tsField && !isMySQL) query += ` ORDER BY ${tsField} DESC`;
    query += ' LIMIT 10000';
    setSql(query);
  }, [sqlIsDirty, selectedTable, selectedColumns, columns, timeRangeTab, specificDirection, specificDate, timeFrom, timeTo, distinctColumn, selectedDistinctValues, dbEngine, filters]);

  // ─────────────────────────────────────────────────────────────────────
  // Action handlers
  // ─────────────────────────────────────────────────────────────────────

  const dbOpts = { dbEngine, mysqlDatabase, mysqlInstanceId };

  const handlePreview = async () => {
    if (!sql || (!selectedPlant && dbEngine !== 'mysql')) return;
    setLoadingPreview(true); setExportError('');
    try {
      const result = await reportService.preview(sql, selectedPlant, dbOpts);
      setPreviewData(result.data); setPreviewColumns(result.columns); setPreviewCount(result.totalRows); setPreviewPage(1);
    } catch (err) { setExportError('Preview failed: ' + err.message); }
    finally { setLoadingPreview(false); }
  };

  const handleExport = async () => {
    if (!sql || (!selectedPlant && dbEngine !== 'mysql')) return;
    if (exportFormat === 'excel' && excelMode === 'append' && !appendFile) {
      setExportError('Please select an existing Excel file to append to'); return;
    }
    setExporting(true); setExportError(''); setExportSuccess('');
    try {
      let blob;
      if (exportFormat === 'excel' && excelMode === 'append' && appendFile) {
        const base64 = await reportService.fileToBase64(appendFile);
        blob = await reportService.exportAppend(base64, sql, selectedPlant, dbOpts);
      } else {
        const result = await reportService.exportReport(sql, selectedPlant, exportFormat, undefined, dbOpts);
        if (result.kind === 'job') {
          setExportSuccess(
            `Result is large (~${(result.estimatedRows || 0).toLocaleString()} rows). ` +
            `Running as background job ${result.jobId.slice(-6)} — watch the Exports inbox for progress.`
          );
          if (typeof loadHistory === 'function') loadHistory();
          return;
        }
        blob = result.blob;
      }
      const ext = exportFormat === 'excel' ? 'xlsx' : exportFormat;
      const filename = (exportFormat === 'excel' && excelMode === 'append' && appendFile)
        ? appendFile.name : `miralys_report_${Date.now()}.${ext}`;
      reportService.downloadBlob(blob, filename);
      setExportSuccess(
        exportLimits
          ? `Downloaded ${filename} (sync stream, under ${exportLimits.asyncPromoteRows.toLocaleString()}-row threshold).`
          : `Downloaded ${filename}`
      );
    } catch (err) { setExportError('Export failed: ' + err.message); }
    finally { setExporting(false); }
  };

  const handleCreateSchedule = async () => {
    if (!sql || (!selectedPlant && dbEngine !== 'mysql') || !scheduleName.trim()) {
      setExportError('Please fill in the report name and SQL query'); return;
    }
    setExporting(true); setExportError(''); setExportSuccess('');
    try {
      let intervalMinutes;
      if (scheduleInterval === 'custom') {
        const m = customUnit === 'hours' ? 60 : customUnit === 'days' ? 1440 : 1;
        intervalMinutes = customInterval * m;
      }
      await reportService.createScheduledReport({
        name: scheduleName.trim(), sql, plantId: selectedPlant,
        // Use exportFormat / excelMode — these are the state vars the format
        // buttons and the New/Append toggle actually write to. The previously
        // referenced scheduleFormat / scheduleExcelMode were ghost state that
        // never updated, so every scheduled report came out as Excel-new.
        format: exportFormat,
        // Field is named excelMode for back-compat, but the server now honors
        // append mode for CSV as well. PDF still always gets a new file.
        excelMode: (exportFormat === 'excel' || exportFormat === 'csv') ? excelMode : 'new',
        schedule: { type: scheduleInterval, intervalMinutes },
        dbEngine, mysqlDatabase, mysqlInstanceId
      });
      setExportSuccess(`Scheduled report "${scheduleName}" created`);
      setScheduleName('');
      loadHistory();
      setShowExportModal(false);
      setView('scheduled');
    } catch (err) { setExportError('Failed to create schedule: ' + err.message); }
    finally { setExporting(false); }
  };

  const handlePauseResume = async (id, status) => {
    try {
      status === 'active'
        ? await reportService.pauseScheduledReport(id)
        : await reportService.resumeScheduledReport(id);
      loadHistory();
    } catch (err) { setExportError('Failed to update schedule: ' + err.message); }
  };

  const handleDeleteSchedule = async (id) => {
    try { await reportService.deleteScheduledReport(id); loadHistory(); }
    catch (err) { setExportError('Failed to delete: ' + err.message); }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const blob = await reportService.downloadReportFile(fileId);
      reportService.downloadBlob(blob, filename);
      // Remove from local list after download (it's deleted server-side)
      setReportFiles(prev => prev.filter(f => f._id !== fileId));
    } catch (err) { setExportError('Download failed: ' + err.message); }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await reportService.deleteReportFile(fileId);
      setReportFiles(prev => prev.filter(f => f._id !== fileId));
    } catch (err) { setExportError('Delete failed: ' + err.message); }
  };

  const toggleColumn = (name) =>
    setSelectedColumns(p => p.includes(name) ? p.filter(c => c !== name) : [...p, name]);

  const toggleDistinctValue = (val) =>
    setSelectedDistinctValues(p => p.includes(val) ? p.filter(v => v !== val) : [...p, val]);

  // ─────────────────────────────────────────────────────────────────────
  // Filtered list data
  // ─────────────────────────────────────────────────────────────────────

  const filteredFiles = reportFiles.filter(f => {
    const matchSearch = !fileSearch || f.filename.toLowerCase().includes(fileSearch.toLowerCase());
    const matchType = !fileTypeFilter || f.format === fileTypeFilter;
    return matchSearch && matchType;
  });

  const filteredScheduled = scheduledReports.filter(r => {
    const matchSearch = !schedSearch || r.name.toLowerCase().includes(schedSearch.toLowerCase());
    const matchType = !schedTypeFilter || r.status === schedTypeFilter;
    return matchSearch && matchType;
  });

  // ─────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────

  const formatBytes = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;
  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const formatTime = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const formatScheduleLabel = (s) => {
    if (!s) return '—';
    if (s.type === 'every_minute') return 'Every minute';
    if (s.type === 'every_hour') return 'Every hour';
    if (s.type === 'every_day') return 'Every day at 9:00 AM';
    if (s.type === 'custom') return `Every ${s.intervalMinutes} min`;
    return s.type;
  };
  const formatBadgeColor = { excel: '#22c55e', csv: '#3b82f6', pdf: '#ef4444' };
  const formatLabel = { excel: 'EXCEL', csv: 'CSV', pdf: 'PDF' };

  // ─────────────────────────────────────────────────────────────────────
  // Nav bar
  // ─────────────────────────────────────────────────────────────────────

  const NavBar = () => (
    <div style={s.navBar}>
      <div style={s.navLeft}>
        <LayoutList size={13} style={{ color: '#6b7280' }} />
        <span style={s.navTitle}>Reports</span>
      </div>
      <div style={s.navRight}>
        <NavBtn active={view === 'new-report'} highlight={view === 'new-report'} onClick={() => setView('new-report')}>
          <FileIcon size={13} /> New Report
        </NavBtn>
        <NavBtn active={view === 'scheduled'} highlight={view === 'scheduled'} onClick={() => setView('scheduled')}>
          <Clock size={13} /> Scheduled
        </NavBtn>
        <NavBtn active={view === 'generated-files'} highlight={view === 'generated-files'} onClick={() => setView('generated-files')}>
          <Download size={13} /> Generated Files
        </NavBtn>
        <LockedFeature feature="templates">
          <NavBtn
            active={view === 'templates'}
            highlight={view === 'templates'}
            onClick={() => authService.hasFeature('templates') && setView('templates')}
          >
            <LayoutList size={13} /> Templates
          </NavBtn>
        </LockedFeature>
        <button style={s.backBtn} onClick={onBack}>
          ← Back
        </button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────
  // Generated Files view
  // ─────────────────────────────────────────────────────────────────────

  const GeneratedFilesView = () => (
    <div>
      <div style={s.sectionHeader}>
        <FileDown size={14} style={{ color: '#a78bfa' }} />
        <span style={s.sectionHeaderText}>Generated Reports</span>
      </div>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <div style={s.filterBarLeft}>
          <Filter size={13} style={{ color: '#6b7280' }} />
          <span style={s.filterLabel}>Filter</span>
          <div style={s.searchWrap}>
            <Search size={12} style={{ color: '#6b7280', position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              style={s.searchInput}
              placeholder="Filter by Tag name"
              value={fileSearch}
              onChange={e => setFileSearch(e.target.value)}
            />
          </div>
          <select
            style={s.typeSelect}
            value={fileTypeFilter}
            onChange={e => setFileTypeFilter(e.target.value)}
          >
            <option value="">Type</option>
            <option value="excel">Excel</option>
            <option value="csv">CSV</option>
            <option value="pdf">PDF</option>
          </select>
          <button style={s.clearBtn} onClick={() => { setFileSearch(''); setFileTypeFilter(''); }}>
            <RefreshCw size={12} /> Clear All
          </button>
        </div>
        <span style={s.totalBadge}>Total: {filteredFiles.length}</span>
      </div>

      {/* List */}
      {loadingHistory && <div style={s.emptyMsg}>Loading...</div>}
      {!loadingHistory && filteredFiles.length === 0 && (
        <div style={s.emptyMsg}>No generated files yet</div>
      )}
      {filteredFiles.map(file => (
        <div key={file._id} style={s.listRow}>
          <div style={s.rowLeft}>
            <div style={s.fileIcon}><ClipboardList size={18} style={{ color: '#a78bfa' }} /></div>
            <div style={s.rowInfo}>
              <div style={s.rowTitle}>
                {file.filename}
                <span style={{ ...s.formatBadge, background: formatBadgeColor[file.format] + '22', color: formatBadgeColor[file.format], border: `1px solid ${formatBadgeColor[file.format]}44` }}>
                  {formatLabel[file.format] || file.format.toUpperCase()}
                </span>
                {file.isExpired && <span style={s.expiredBadge}>EXPIRED</span>}
              </div>
              <div style={s.rowMeta}>
                <span style={s.rowMetaItem}><FileDown size={13} /> {formatBytes(file.size)}</span>
                <span style={s.rowMetaItem}><Clock size={13} /> {formatTime(file.createdAt)}</span>
                <span style={s.rowMetaItem}><Calendar size={13} /> {formatDate(file.createdAt)}</span>
                <span style={s.rowMetaItem}><LayoutList size={13} /> {file.rowCount} Rows</span>
                {file.expiresAt && !file.isExpired && (
                  <span style={{ color: '#f59e0b' }}>
                    Expires {formatDate(file.expiresAt)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={s.rowActions}>
            {!file.isExpired && (
              <button style={s.exportActionBtn} onClick={() => handleDownloadFile(file._id, file.filename)}>
                <Download size={13} /> Export
              </button>
            )}
            <button style={s.deleteActionBtn} onClick={() => handleDeleteFile(file._id)}>
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────
  // Scheduled Reports view
  // ─────────────────────────────────────────────────────────────────────

  const ScheduledView = () => (
    <div>
      <div style={s.sectionHeader}>
        <Clock size={14} style={{ color: '#a78bfa' }} />
        <span style={s.sectionHeaderText}>Scheduled Reports</span>
      </div>

      <div style={s.filterBar}>
        <div style={s.filterBarLeft}>
          <Filter size={13} style={{ color: '#6b7280' }} />
          <span style={s.filterLabel}>Filter</span>
          <div style={s.searchWrap}>
            <Search size={12} style={{ color: '#6b7280', position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              style={s.searchInput}
              placeholder="Filter by Tag name"
              value={schedSearch}
              onChange={e => setSchedSearch(e.target.value)}
            />
          </div>
          <select style={s.typeSelect} value={schedTypeFilter} onChange={e => setSchedTypeFilter(e.target.value)}>
            <option value="">Type</option>
            <option value="active">Active</option>
            <option value="paused">Inactive</option>
          </select>
          <button style={s.clearBtn} onClick={() => { setSchedSearch(''); setSchedTypeFilter(''); }}>
            <RefreshCw size={12} /> Clear All
          </button>
        </div>
        <span style={s.totalBadge}>Total: {filteredScheduled.length}</span>
      </div>

      {loadingHistory && <div style={s.emptyMsg}>Loading...</div>}
      {!loadingHistory && filteredScheduled.length === 0 && (
        <div style={s.emptyMsg}>No scheduled reports yet</div>
      )}
      {filteredScheduled.map(r => (
        <div key={r._id} style={s.schedRow}>
          {/* Line 1: icon + name + badge + actions */}
          <div style={s.schedTopRow}>
            <div style={s.schedTopLeft}>
              <div style={s.schedFileIcon}><ClipboardList size={17} style={{ color: '#a78bfa' }} /></div>
              <span style={s.schedName}>{r.name}</span>
              <span style={r.status === 'active' ? s.activeBadge : s.inactiveBadge}>
                {r.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div style={s.schedActions}>
              <button style={s.schedEditBtn} onClick={() => handlePauseResume(r._id, r.status)}>
                <Edit2 size={11} /> {r.status === 'active' ? 'Pause' : 'Resume'}
              </button>
              <button style={s.schedDeleteBtn} onClick={() => handleDeleteSchedule(r._id)}>
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </div>
          {/* Line 2: 4 meta columns full width */}
          <div style={s.schedMeta}>
            <div style={s.schedMetaCol}>
              <span style={s.schedMetaLabel}>SCHEDULE</span>
              <span style={s.schedMetaValue}>{formatScheduleLabel(r.schedule)}</span>
            </div>
            <div style={s.schedMetaCol}>
              <span style={s.schedMetaLabel}>TYPE</span>
              <span style={s.schedMetaValue}>{r.excelMode === 'append' ? 'Append' : 'New File'}</span>
            </div>
            <div style={s.schedMetaCol}>
              <span style={s.schedMetaLabel}>LAST RUN</span>
              <span style={s.schedMetaValue}>
                {r.lastRun ? `${formatDate(r.lastRun)} • ${formatTime(r.lastRun)}` : '—'}
              </span>
            </div>
            <div style={s.schedMetaCol}>
              <span style={s.schedMetaLabel}>NEXT RUN</span>
              <span style={s.schedMetaValue}>
                {r.nextRun ? `${formatDate(r.nextRun)} • ${formatTime(r.nextRun)}` : '—'}
              </span>
            </div>
          </div>
          {r.lastError && <div style={s.errorText}>{r.lastError}</div>}
        </div>
      ))}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────
  // New Report view
  // ─────────────────────────────────────────────────────────────────────

  const NewReportView = () => (
    <div style={s.newReportGrid}>
      {/* LEFT PANEL */}
      <div style={s.leftPanel}>
        {/* Scrollable section — everything except the Preview button */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* Date Source section */}
        <div style={s.panel}>
          <button style={s.panelHeader} onClick={() => setDataSourceOpen(o => !o)}>
            <div style={s.panelHeaderLeft}>
              <Database size={14} style={{ color: '#a78bfa' }} />
              <span style={s.panelHeaderText}>Date Source</span>
            </div>
            {dataSourceOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {dataSourceOpen && (
            <div style={s.panelBody}>
              <label style={s.fieldLabel}>Database Engine</label>
              <div style={{ display: 'flex', gap: '0', borderRadius: 7, overflow: 'hidden', border: '1px solid #1a2540', background: '#0b1220' }}>
                {[{ id: 'questdb', label: 'QuestDB' }, { id: 'mysql', label: 'MySQL' }].map((eng, i) => (
                  <button
                    key={eng.id}
                    onClick={() => { setDbEngine(eng.id); setSelectedTable(''); setColumns([]); setSelectedColumns([]); }}
                    style={{
                      flex: 1, padding: '7px 0', border: 'none', cursor: 'pointer',
                      fontSize: 12, fontWeight: 500, transition: 'all 0.15s ease',
                      borderRight: i === 0 ? '1px solid #1a2540' : 'none',
                      background: dbEngine === eng.id ? '#6366f1' : 'transparent',
                      color: dbEngine === eng.id ? '#fff' : '#6b7280',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5
                    }}
                  >
                    <Database size={12} />
                    {eng.label}
                  </button>
                ))}
              </div>

              {/* MySQL reports also need the plant: it decides which data sources may be used */}
              <label style={s.fieldLabel}>Plant</label>
              <select style={s.fieldSelect} value={selectedPlant} onChange={e => { setSelectedPlant(e.target.value); setMysqlDatabase(''); setSelectedTable(''); }}>
                {plantAccess.map(pa => <option key={pa.plantId} value={pa.plantId}>{pa.plantId}</option>)}
              </select>

              {dbEngine === 'mysql' && (
                <>
                  <label style={s.fieldLabel}>Data source</label>
                  <select style={s.fieldSelect} data-testid="report-mysql-instance" value={mysqlInstanceId}
                          onChange={e => { setMysqlInstanceId(e.target.value); setMysqlDatabase(''); setSelectedTable(''); }}>
                    <option value="">— Select data source —</option>
                    {mysqlInstances.map(inst => (
                      <option key={inst.instanceId} value={inst.instanceId}>{inst.label || inst.instanceId}{inst.isReadReplica ? ' (read replica)' : ''}</option>
                    ))}
                  </select>
                  <label style={s.fieldLabel}>Database</label>
                  <select style={s.fieldSelect} value={mysqlDatabase} disabled={!mysqlInstanceId}
                          onChange={e => { setMysqlDatabase(e.target.value); setSelectedTable(''); }}>
                    <option value="">{mysqlInstanceId ? '— Select database —' : 'pick a data source first'}</option>
                    {mysqlDatabases.map(db => <option key={db} value={db}>{db}</option>)}
                  </select>
                </>
              )}

              <label style={s.fieldLabel}>Table</label>
              <select style={s.fieldSelect} value={selectedTable} onChange={e => setSelectedTable(e.target.value)} disabled={loadingTables}>
                <option value="">{loadingTables ? 'Loading...' : '— Select table —'}</option>
                {tables.map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              {columns.length > 0 && (
                <>
                  <label style={s.fieldLabel}>Columns</label>
                  <div style={s.searchWrapFull}>
                    <Search size={12} style={{ color: '#6b7280', position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
                    <input style={s.searchInputFull} placeholder="Search" value={columnSearch} onChange={e => setColumnSearch(e.target.value)} />
                  </div>
                  <div style={s.columnList}>
                    {columns.filter(c => !columnSearch || c.name.toLowerCase().includes(columnSearch.toLowerCase())).map(col => (
                      <label key={col.name} style={s.checkRow}>
                        <input type="checkbox" checked={selectedColumns.includes(col.name)} onChange={() => toggleColumn(col.name)} style={{ accentColor: '#6366f1' }} />
                        <span style={s.colName}>{col.name}</span>
                        <span style={s.colType}>{col.type}</span>
                      </label>
                    ))}
                  </div>

                  <label style={s.fieldLabel}>Filter by Distinct</label>
                  <select style={s.fieldSelect} value={distinctColumn} onChange={e => setDistinctColumn(e.target.value)}>
                    <option value="">— Select column —</option>
                    {columns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>

                  {loadingDistinct && <div style={s.hint}>Loading...</div>}

                  {selectedDistinctValues.length > 0 && (
                    <div style={s.pillRow}>
                      <span style={s.pillLabel}>Selected Distincts</span>
                      <div style={s.pills}>
                        {selectedDistinctValues.map(v => (
                          <button key={v} style={s.pillActive} onClick={() => toggleDistinctValue(v)}>
                            <X size={10} /> {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {distinctValues.length > 0 && (
                    <div style={s.pillRow}>
                      <span style={s.pillLabel}>Available Distincts</span>
                      {/* Search bar */}
                      <div style={s.searchWrapFull}>
                        <Search size={12} style={{ color: '#6b7280', position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                          style={s.searchInputFull}
                          placeholder="Search values..."
                          value={distinctSearch}
                          onChange={e => setDistinctSearch(e.target.value)}
                        />
                      </div>
                      {/* Scrollable pill container */}
                      <div style={s.distinctScroll}>
                        {distinctValues
                          .filter(v => !selectedDistinctValues.includes(String(v)))
                          .filter(v => !distinctSearch || String(v).toLowerCase().includes(distinctSearch.toLowerCase()))
                          .map((v, i) => (
                            <button key={i} style={s.pill} onClick={() => toggleDistinctValue(String(v))}>
                              + {String(v)}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Filters section — panel-style WHERE builder (AND only) */}
        {columns.length > 0 && (
          <div style={s.panel}>
            <button style={s.panelHeader} onClick={() => setFiltersOpen(o => !o)}>
              <div style={s.panelHeaderLeft}>
                <Filter size={14} style={{ color: '#a78bfa' }} />
                <span style={s.panelHeaderText}>
                  Filters{filters.length ? ` (${filters.length})` : ''}
                </span>
              </div>
              {filtersOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {filtersOpen && (
              <div style={s.panelBody}>
                <div style={{ ...s.hint, marginBottom: 8 }}>
                  Combined with <strong>AND</strong>. Empty rows are ignored.
                </div>
                {filters.length === 0 && (
                  <div style={{
                    padding: '10px 12px', fontSize: 12, color: '#6b7280',
                    fontStyle: 'italic', background: '#0a0e1a', borderRadius: 6
                  }}>
                    No filters — every row in the table is considered.
                  </div>
                )}
                {filters.map((f, i) => (
                  <div key={i} style={{
                    marginTop: 8, padding: 8, borderRadius: 6,
                    background: '#0a0e1a', border: '1px solid #1e293b',
                    display: 'flex', flexDirection: 'column', gap: 6
                  }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select
                        style={{ ...s.fieldSelect, flex: 1, minWidth: 0, padding: '6px 8px' }}
                        value={f.field || ''}
                        onChange={e => setFilters(prev => prev.map((x, j) => j === i ? { ...x, field: e.target.value } : x))}
                      >
                        <option value="">field…</option>
                        {columns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                      <button
                        onClick={() => setFilters(prev => prev.filter((_, j) => j !== i))}
                        style={{
                          background: 'transparent', border: '1px solid #1e293b',
                          borderRadius: 6, padding: '5px 7px', color: '#f87171',
                          cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }}
                        title="Remove filter"
                      ><Trash2 size={12} /></button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select
                        style={{ ...s.fieldSelect, width: 92, flexShrink: 0, padding: '6px 8px' }}
                        value={f.op || '='}
                        onChange={e => setFilters(prev => prev.map((x, j) => j === i ? { ...x, op: e.target.value } : x))}
                      >
                        <option value="=">=</option>
                        <option value="!=">≠</option>
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                        <option value=">=">≥</option>
                        <option value="<=">≤</option>
                        <option value="LIKE">LIKE</option>
                        <option value="IN">IN</option>
                        <option value="NOT IN">NOT IN</option>
                        <option value="BETWEEN">BETWEEN</option>
                        <option value="IS NULL">is null</option>
                        <option value="IS NOT NULL">not null</option>
                      </select>
                      <input
                        style={{ ...s.fieldSelect, flex: 1, minWidth: 0, padding: '6px 8px' }}
                        placeholder={
                          (f.op === 'IS NULL' || f.op === 'IS NOT NULL') ? '—' :
                          (f.op === 'IN' || f.op === 'NOT IN') ? 'a, b, c' :
                          f.op === 'BETWEEN' ? 'low, high' : 'value'
                        }
                        disabled={f.op === 'IS NULL' || f.op === 'IS NOT NULL'}
                        value={(f.op === 'IS NULL' || f.op === 'IS NOT NULL') ? '' : (f.value ?? '')}
                        onChange={e => setFilters(prev => prev.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                      />
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setFilters(prev => [...prev, { field: '', op: '=', value: '' }])}
                  style={{
                    marginTop: 10, width: '100%', padding: '8px 10px',
                    background: 'transparent', border: '1px dashed #334155',
                    borderRadius: 6, color: '#a78bfa', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6
                  }}
                >
                  <Plus size={12} /> Add filter
                </button>
              </div>
            )}
          </div>
        )}

        {/* Time Range section — only meaningful for QuestDB (timestamp column) */}
        {dbEngine !== 'mysql' && (
        <div style={s.panel}>
          <button style={s.panelHeader} onClick={() => setTimeRangeOpen(o => !o)}>
            <div style={s.panelHeaderLeft}>
              <Clock size={14} style={{ color: '#a78bfa' }} />
              <span style={s.panelHeaderText}>Time Range</span>
            </div>
            {timeRangeOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>

          {timeRangeOpen && (
            <div style={s.panelBody}>
              {/* Tab toggle */}
              <div style={s.tabToggle}>
                <button style={timeRangeTab === 'specific' ? s.tabActive : s.tabInactive} onClick={() => setTimeRangeTab('specific')}>
                  Specific Date
                </button>
                <button style={timeRangeTab === 'duration' ? s.tabActive : s.tabInactive} onClick={() => setTimeRangeTab('duration')}>
                  Duration
                </button>
              </div>

              {timeRangeTab === 'specific' && (
                <div style={s.dateRow}>
                  <select style={s.dirSelect} value={specificDirection} onChange={e => setSpecificDirection(e.target.value)}>
                    <option>Before</option>
                    <option>After</option>
                  </select>
                  <input type="date" style={s.dateInput} value={specificDate} onChange={e => setSpecificDate(e.target.value)} />
                </div>
              )}

              {timeRangeTab === 'duration' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={s.fieldLabel}>From</label>
                  <div style={s.dateRow}>
                    <select style={s.dirSelect}>
                      <option>Before</option>
                      <option>After</option>
                    </select>
                    <input type="date" style={s.dateInput} value={timeFrom} onChange={e => setTimeFrom(e.target.value)} />
                  </div>
                  <label style={s.fieldLabel}>To</label>
                  <div style={s.dateRow}>
                    <select style={s.dirSelect}>
                      <option>Before</option>
                      <option>After</option>
                    </select>
                    <input type="date" style={s.dateInput} value={timeTo} onChange={e => setTimeTo(e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        </div>{/* end scrollable */}

        {/* Preview button — pinned at bottom */}
        <button style={s.previewBtn} onClick={handlePreview} disabled={!sql || loadingPreview}>
          {loadingPreview ? <RefreshCw size={14} className="spin" /> : <Eye size={14} />}
          {loadingPreview ? 'Loading...' : 'Preview'}
        </button>
      </div>

      {/* RIGHT PANEL */}
      <div style={s.rightPanel}>
        {/* SQL section */}
        <div style={{ ...s.panel, flexShrink: 0 }}>
          <button style={s.panelHeader} onClick={() => setSqlOpen(o => !o)}>
            <div style={s.panelHeaderLeft}>
              <span style={{ color: '#22d3ee', fontSize: 12, fontWeight: 700 }}>{'{}'}</span>
              <span style={s.panelHeaderText}>
                SQL Query
                {sqlIsDirty && (
                  <span style={{
                    marginLeft: 8, fontSize: 10, fontWeight: 500,
                    color: '#fbbf24', background: 'rgba(251,191,36,0.12)',
                    padding: '2px 6px', borderRadius: 4
                  }}>edited</span>
                )}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {sqlIsDirty && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setSqlIsDirty(false);
                  }}
                  style={{
                    fontSize: 11, color: '#a78bfa', cursor: 'pointer',
                    padding: '3px 8px', border: '1px solid #334155',
                    borderRadius: 5, background: 'transparent'
                  }}
                  title="Regenerate the query from your column / filter / time-range selections"
                >Reset SQL</span>
              )}
              {sqlOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
          </button>
          {sqlOpen && (
            <div style={s.panelBody}>
              <textarea
                style={s.sqlEditor}
                value={sql}
                onChange={e => { setSql(e.target.value); if (!sqlIsDirty) setSqlIsDirty(true); }}
                placeholder="SELECT * FROM your_table ORDER BY timestamp DESC LIMIT 1000"
                rows={4}
                spellCheck={false}
              />
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                {sqlIsDirty
                  ? 'This is your query — selections on the left no longer touch it. Hit Reset SQL to regenerate.'
                  : 'Auto-generated from your selections. Start typing here to take over.'}
              </div>
            </div>
          )}
        </div>

        {/* Error display */}
        {exportError && !showExportModal && (
          <div style={{ padding: '8px 12px', margin: '0 0 8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertCircle size={13} /> {exportError}
          </div>
        )}

        {/* Preview table */}
        {previewData.length > 0 && (
          <div style={{ ...s.panel, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={s.panelHeaderStatic}>
              <div style={s.panelHeaderLeft}>
                <Eye size={14} style={{ color: '#a78bfa' }} />
                <span style={s.panelHeaderText}>Preview</span>
              </div>
              <button style={s.exportInlineBtn} onClick={() => setShowExportModal(true)}>
                <Download size={13} /> Export
              </button>
            </div>
            <div style={s.tableWrap}>
              <table style={s.dataTable}>
                <thead>
                  <tr>
                    {previewColumns.map(col => (
                      <th key={col.name} style={s.th}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {col.name}
                          <ChevronDown size={10} style={{ color: '#4b5563', flexShrink: 0 }} />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData
                    .slice((previewPage - 1) * PREVIEW_PAGE_SIZE, previewPage * PREVIEW_PAGE_SIZE)
                    .map((row, i) => (
                      <tr key={i} style={i % 2 === 0 ? { background: 'rgba(15,23,42,0.5)' } : {}}>
                        {previewColumns.map(col => (
                          <td key={col.name} style={s.td}>
                            {row[col.name] !== null && row[col.name] !== undefined ? String(row[col.name]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <Paginator
              page={previewPage}
              total={previewData.length}
              pageSize={PREVIEW_PAGE_SIZE}
              onChange={setPreviewPage}
            />
          </div>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────
  // Root render
  // ─────────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      <div style={s.card}>
        <NavBar />
        <div style={s.cardBody}>
          {view === 'generated-files' && <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}><GeneratedFilesView /></div>}
          {view === 'scheduled' && <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}><ScheduledView /></div>}
          {view === 'new-report' && <NewReportView />}
          {view === 'templates' && (
            authService.hasFeature('templates') ? (
              <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                <TemplatesView defaultPlant={selectedPlant} />
              </div>
            ) : (
              <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                color: '#94a3b8', padding: 40
              }}>
                <Lock size={36} color="#fbbf24" />
                <div style={{ fontSize: 16, fontWeight: 600, color: '#e4e6eb' }}>
                  Report Templates is locked
                </div>
                <div style={{ fontSize: 13, textAlign: 'center', maxWidth: 420 }}>
                  Templates lets you save reusable Excel layouts with cell bindings.
                  Ask an admin to enable the <b>templates</b> feature on your account.
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Export / Schedule modal */}
      {showExportModal && (
        <div style={s.modalOverlay} onClick={() => setShowExportModal(false)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={s.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Download size={15} style={{ color: '#a78bfa' }} />
                <span style={s.modalTitle}>Export / Schedule</span>
              </div>
              <button style={s.modalClose} onClick={() => setShowExportModal(false)}>
                <X size={16} />
              </button>
            </div>

            <div style={s.modalBody}>
              {/* Row 1: Format */}
              <div style={s.formatRow}>
                {[['excel', 'Excel', <FileSpreadsheet size={14} />], ['csv', 'CSV', <FileText size={14} />], ['pdf', 'PDF', <FileDown size={14} />]].map(([fmt, label, icon]) => (
                  <button key={fmt} style={exportFormat === fmt ? s.fmtBtnActive : s.fmtBtn} onClick={() => setExportFormat(fmt)}>
                    {icon} {label}
                  </button>
                ))}
              </div>

              {/* Row 2: New/Append toggle — only for Excel or CSV + Scheduled mode */}
              {(exportFormat === 'excel' || exportFormat === 'csv') && scheduleMode === 'scheduled' && (
                <div style={s.tabToggle}>
                  <button
                    style={excelMode === 'new' ? s.tabActive : s.tabInactive}
                    onClick={() => setExcelMode('new')}
                  >
                    New File
                  </button>
                  <button
                    style={excelMode === 'append' ? s.tabActive : s.tabInactive}
                    onClick={() => setExcelMode('append')}
                  >
                    Append to File
                  </button>
                </div>
              )}

              {/* Row 3: One-time / Schedule toggle */}
              <div style={s.tabToggle}>
                <button style={scheduleMode === 'onetime' ? s.tabActive : s.tabInactive} onClick={() => setScheduleMode('onetime')}>One-time Export</button>
                <button style={scheduleMode === 'scheduled' ? s.tabActive : s.tabInactive} onClick={() => setScheduleMode('scheduled')}>Schedule</button>
              </div>

              {/* Row 4: Mode-specific fields — fixed min-height so modal doesn't resize */}
              <div style={{ minHeight: 120, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {scheduleMode === 'onetime' && (
                  <>
                    {exportLimits && dbEngine === 'mysql' && (exportFormat === 'csv' || exportFormat === 'excel') && (
                      <div style={{
                        fontSize: 11, lineHeight: 1.4, padding: '8px 10px',
                        background: '#0f1a2e', border: '1px solid #1e3a5f', borderRadius: 4,
                        color: '#8ab4ff'
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>How Export Now works</div>
                        <div>• Server runs <code>EXPLAIN</code> on your query first.</div>
                        <div>• If estimated rows ≤ <b>{exportLimits.asyncPromoteRows.toLocaleString()}</b> → streamed straight to your browser (instant download).</div>
                        <div>• If above → promoted to a <b>background job</b>. Still returns <b>all rows</b>, downloadable from the Exports inbox below.</div>
                        <div style={{ marginTop: 4, opacity: 0.75 }}>
                          Limits: timeout {Math.round(exportLimits.timeoutMs / 1000)}s · max {exportLimits.maxRunningPerUser} jobs in flight per user · PDF capped at {exportLimits.pdfMaxRows.toLocaleString()} rows.
                        </div>
                      </div>
                    )}
                    <button
                      style={{ ...s.actionBtn, opacity: (!sql || exporting) ? 0.5 : 1, marginTop: 'auto' }}
                      onClick={async () => { await handleExport(); if (!exportError) setShowExportModal(false); }}
                      disabled={!sql || exporting}
                    >
                      {exporting ? <RefreshCw size={14} /> : <Download size={14} />}
                      {exporting ? 'Exporting...' : 'Export Now'}
                    </button>
                  </>
                )}

                {scheduleMode === 'scheduled' && (
                  <>
                    <input style={s.fieldInput} placeholder="Report name..." value={scheduleName} onChange={e => setScheduleName(e.target.value)} />
                    <select style={s.fieldSelect} value={scheduleInterval} onChange={e => setScheduleInterval(e.target.value)}>
                      <option value="every_minute">Every Minute</option>
                      <option value="every_hour">Every Hour</option>
                      <option value="every_day">Every Day</option>
                      <option value="custom">Custom</option>
                    </select>
                    {scheduleInterval === 'custom' && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="number" min="1" style={{ ...s.fieldInput, width: 80 }} value={customInterval} onChange={e => setCustomInterval(parseInt(e.target.value) || 1)} />
                        <select style={s.fieldSelect} value={customUnit} onChange={e => setCustomUnit(e.target.value)}>
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    )}
                    <button
                      style={{ ...s.actionBtn, opacity: (!sql || exporting || !scheduleName.trim()) ? 0.5 : 1 }}
                      onClick={async () => { await handleCreateSchedule(); }}
                      disabled={!sql || exporting || !scheduleName.trim()}
                    >
                      {exporting ? <RefreshCw size={14} /> : <Calendar size={14} />}
                      {exporting ? 'Creating...' : 'Create Schedule'}
                    </button>
                  </>
                )}
              </div>

              {exportError && <div style={s.errMsg}><X size={12} /> {exportError}</div>}
              {exportSuccess && <div style={s.okMsg}><Check size={12} /> {exportSuccess}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Nav button helper
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Paginator component  (matches screenshot style)
// ─────────────────────────────────────────────────────────────────────────────

function Paginator({ page, total, pageSize, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  // Build page number list with ellipsis: always show first 3, last 3, and ±1 around current
  const getPages = () => {
    const pages = [];
    const delta = 1;
    const left = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i <= right)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '…') {
        pages.push('…');
      }
    }
    return pages;
  };

  const btnBase = {
    minWidth: 28, height: 28,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 6,
    border: '1px solid #1a2540',
    background: 'transparent',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '0 6px'
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 14px', borderTop: '1px solid #1a2540' }}>
      {/* ← */}
      <button style={{ ...btnBase, color: page === 1 ? '#1a2540' : '#6b7280' }} onClick={() => page > 1 && onChange(page - 1)} disabled={page === 1}>
        ←
      </button>

      {getPages().map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} style={{ ...btnBase, border: 'none', cursor: 'default', color: '#374151' }}>…</span>
        ) : (
          <button
            key={p}
            style={{
              ...btnBase,
              background: p === page ? 'rgba(99,102,241,0.8)' : 'transparent',
              borderColor: p === page ? '#6366f1' : '#1a2540',
              color: p === page ? '#fff' : '#6b7280'
            }}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        )
      )}

      {/* → */}
      <button style={{ ...btnBase, color: page === totalPages ? '#1a2540' : '#6b7280' }} onClick={() => page < totalPages && onChange(page + 1)} disabled={page === totalPages}>
        →
      </button>
    </div>
  );
}

function NavBtn({ children, active, onClick, highlight }) {
  // "highlight" = the currently selected view button (filled blue like the screenshot)
  const isHighlighted = active && highlight;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 12px',
        height: 30,
        background: isHighlighted
          ? 'rgba(99,102,241,0.85)'
          : active
            ? 'rgba(255,255,255,0.06)'
            : 'transparent',
        border: isHighlighted
          ? '1px solid rgba(99,102,241,0.6)'
          : active
            ? '1px solid #1e2d45'
            : '1px solid transparent',
        borderRadius: 6,
        color: isHighlighted ? '#fff' : active ? '#c4b5fd' : '#6b7280',
        fontSize: 12,
        fontWeight: isHighlighted ? 600 : 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const s = {
  page: {
    height: '100vh',
    background: '#080d18',
    padding: '16px 20px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  card: {
    background: '#0d1526',
    border: '1px solid #1a2540',
    borderRadius: 14,
    overflow: 'hidden',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: 0  // force flex child to not exceed parent
  },
  cardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    height: 0  // force flex child to not exceed parent
  },

  // Nav bar
  navBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    borderBottom: '1px solid #1a2540',
    minHeight: 42
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 7
  },
  navTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#9ca3af'
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '5px 12px',
    background: 'transparent',
    border: '1px solid #1e2d45',
    borderRadius: 6,
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    height: 30
  },

  // Section header (inside card body)
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 20px 10px'
  },
  sectionHeaderText: {
    fontSize: 22,
    fontWeight: 700,
    color: '#e5e7eb',
    letterSpacing: '0.1px'
  },

  // Filter bar
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 20px',
    background: '#0b1220',
    borderTop: '1px solid #1a2540',
    borderBottom: '1px solid #1a2540',
    gap: 12
  },
  filterBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  filterLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: 600
  },
  searchWrap: {
    position: 'relative',
    width: 180
  },
  searchInput: {
    width: '100%',
    padding: '6px 10px 6px 28px',
    background: '#0f1a2e',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#e5e7eb',
    fontSize: 12,
    boxSizing: 'border-box'
  },
  typeSelect: {
    padding: '6px 28px 6px 10px',
    background: '#0f1a2e',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#9ca3af',
    fontSize: 12
  },
  clearBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#6b7280',
    fontSize: 12,
    cursor: 'pointer'
  },
  totalBadge: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },

  // List rows
  listRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #111827',
    gap: 16
  },
  rowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: 1,
    minWidth: 0
  },
  fileIcon: {
    width: 44,
    height: 44,
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  rowInfo: {
    flex: 1,
    minWidth: 0
  },
  rowTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 6
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    flexWrap: 'wrap',
    fontSize: 14,
    color: '#6b7280'
  },
  rowMetaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0
  },

  // Badges
  formatBadge: {
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700
  },
  activeBadge: {
    padding: '3px 12px',
    background: 'rgba(34,197,94,0.12)',
    border: '1px solid rgba(34,197,94,0.25)',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    color: '#22c55e',
    whiteSpace: 'nowrap'
  },
  inactiveBadge: {
    padding: '3px 12px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    color: '#ef4444',
    whiteSpace: 'nowrap'
  },
  expiredBadge: {
    padding: '2px 8px',
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    color: '#ef4444'
  },

  // Scheduled row layout
  schedRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '16px 20px',
    borderBottom: '1px solid #0f1624'
  },
  schedTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  schedTopLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  schedRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0
  },
  schedFileIcon: {
    width: 44,
    height: 44,
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  schedRowBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  schedTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  schedName: {
    fontSize: 22,
    fontWeight: 700,
    color: '#ffffff'
  },
  // Scheduled meta columns
  schedMeta: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%'
  },
  schedMetaCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  schedMetaLabel: {
    fontSize: 18,
    color: '#4b5563',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px'
  },
  schedMetaValue: {
    fontSize: 18,
    color: '#9ca3af',
    lineHeight: 1.4
  },
  schedActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0
  },
  schedEditBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 12px',
    background: 'transparent',
    border: '1px solid #1e2d45',
    borderRadius: 6,
    color: '#a78bfa',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer'
  },
  schedDeleteBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 12px',
    background: 'transparent',
    border: '1px solid #1e2d45',
    borderRadius: 6,
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer'
  },
  errorText: {
    fontSize: 11,
    color: '#ef4444',
    marginTop: 2
  },

  // Action buttons in rows
  exportActionBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '7px 16px',
    background: 'rgba(99,102,241,0.75)',
    border: '1px solid rgba(99,102,241,0.5)',
    borderRadius: 7,
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  },
  editActionBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 14px',
    background: 'rgba(167,139,250,0.12)',
    border: '1px solid rgba(167,139,250,0.25)',
    borderRadius: 7,
    color: '#a78bfa',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },
  deleteActionBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },

  emptyMsg: {
    textAlign: 'center',
    padding: '32px 0',
    color: '#374151',
    fontSize: 13
  },

  // ── New Report grid ──────────────────────────────────────────────────
  newReportGrid: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gridTemplateRows: '1fr',
    gap: 0,
    flex: 1,
    minHeight: 0,
    borderTop: '1px solid #1a2540',
    overflow: 'hidden'
  },
  leftPanel: {
    borderRight: '1px solid #1a2540',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  },
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  },

  // Collapsible panels
  panel: {
    borderBottom: '1px solid #1a2540'
  },
  panelHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af'
  },
  panelHeaderStatic: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #1a2540'
  },
  panelHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  panelHeaderText: {
    fontSize: 13,
    fontWeight: 700,
    color: '#e5e7eb'
  },
  panelBody: {
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },

  fieldLabel: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 600,
    marginBottom: -2
  },
  fieldSelect: {
    width: '100%',
    padding: '8px 10px',
    background: '#0b1220',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#e5e7eb',
    fontSize: 12
  },
  fieldInput: {
    width: '100%',
    padding: '8px 10px',
    background: '#0b1220',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#e5e7eb',
    fontSize: 12,
    boxSizing: 'border-box'
  },

  searchWrapFull: {
    position: 'relative',
    width: '100%'
  },
  searchInputFull: {
    width: '100%',
    padding: '7px 10px 7px 28px',
    background: '#0b1220',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#e5e7eb',
    fontSize: 12,
    boxSizing: 'border-box'
  },

  columnList: {
    maxHeight: 180,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '3px 4px',
    fontSize: 12,
    cursor: 'pointer',
    borderRadius: 5
  },
  colName: {
    flex: 1,
    color: '#e5e7eb',
    fontSize: 12
  },
  colType: {
    fontSize: 10,
    color: '#4b5563',
    padding: '1px 5px',
    background: '#111827',
    borderRadius: 4
  },

  pillRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5
  },
  pillLabel: {
    fontSize: 10,
    color: '#4b5563',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5
  },
  distinctScroll: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    maxHeight: 180,
    overflowY: 'auto',
    paddingRight: 2
  },
  pill: {
    padding: '3px 9px',
    background: '#131f35',
    border: '1px solid #1a2540',
    borderRadius: 14,
    color: '#6b7280',
    fontSize: 11,
    cursor: 'pointer'
  },
  pillActive: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '3px 9px',
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid #6366f1',
    borderRadius: 14,
    color: '#a78bfa',
    fontSize: 11,
    cursor: 'pointer'
  },

  hint: {
    fontSize: 11,
    color: '#4b5563',
    fontStyle: 'italic'
  },

  // Time range
  tabToggle: {
    display: 'flex',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #1a2540'
  },
  tabActive: {
    flex: 1,
    padding: '7px 10px',
    background: 'rgba(99,102,241,0.18)',
    border: 'none',
    outline: 'none',
    color: '#a78bfa',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer'
  },
  tabInactive: {
    flex: 1,
    padding: '7px 10px',
    background: '#0b1220',
    border: 'none',
    outline: 'none',
    color: '#4b5563',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },
  dateRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center'
  },
  dirSelect: {
    padding: '7px 10px',
    background: '#0b1220',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#9ca3af',
    fontSize: 12,
    minWidth: 80
  },
  dateInput: {
    flex: 1,
    padding: '7px 10px',
    background: '#0b1220',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#e5e7eb',
    fontSize: 12
  },

  // Preview button (bottom of left panel)
  previewBtn: {
    margin: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '11px 16px',
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    border: 'none',
    borderRadius: 9,
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(99,102,241,0.3)'
  },

  // SQL editor
  sqlEditor: {
    width: '100%',
    minHeight: 90,
    padding: 12,
    background: '#080d18',
    border: '1px solid #1a2540',
    borderRadius: 7,
    color: '#22d3ee',
    fontSize: 12,
    fontFamily: 'monospace',
    resize: 'vertical',
    boxSizing: 'border-box',
    lineHeight: 1.6
  },

  // Preview table
  tableWrap: {
    flex: 1,
    overflowX: 'auto',
    overflowY: 'auto'
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
    whiteSpace: 'nowrap'
  },
  th: {
    padding: '9px 12px',
    textAlign: 'left',
    background: '#111827',
    color: '#a78bfa',
    fontWeight: 700,
    position: 'sticky',
    top: 0,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    borderBottom: '1px solid #1a2540'
  },
  td: {
    padding: '7px 12px',
    borderBottom: '1px solid #0d1526',
    color: '#cbd5e1',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  exportInlineBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px',
    background: 'rgba(59,130,246,0.15)',
    border: '1px solid rgba(59,130,246,0.3)',
    borderRadius: 7,
    color: '#3b82f6',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },

  // Format buttons
  formatRow: {
    display: 'flex',
    gap: 8
  },
  fmtBtn: {
    flex: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '9px 8px',
    background: '#0b1220',
    border: '1px solid #1a2540',
    borderRadius: 8,
    color: '#4b5563',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },
  fmtBtnActive: {
    flex: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '9px 8px',
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid #6366f1',
    borderRadius: 8,
    color: '#a78bfa',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer'
  },

  actionBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '11px 16px',
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    border: 'none',
    borderRadius: 9,
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(99,102,241,0.25)'
  },

  errMsg: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 12px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 7,
    color: '#ef4444',
    fontSize: 12
  },
  okMsg: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 12px',
    background: 'rgba(34,197,94,0.1)',
    border: '1px solid rgba(34,197,94,0.25)',
    borderRadius: 7,
    color: '#22c55e',
    fontSize: 12
  },

  // ── Export modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalBox: {
    background: '#0d1526',
    border: '1px solid #1a2540',
    borderRadius: 14,
    width: 480,
    maxWidth: '94vw',
    boxShadow: '0 24px 60px rgba(0,0,0,0.6)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #1a2540'
  },
  modalTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e5e7eb'
  },
  modalClose: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28,
    background: 'transparent',
    border: '1px solid #1a2540',
    borderRadius: 6,
    color: '#6b7280',
    cursor: 'pointer',
    padding: 0
  },
  modalBody: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  }
};
